import moment from "moment"
import _ from "lodash"

// Format Date used to save dates into state
export const standardFormatDateTime = "YYYY-MM-DDTHH:mm:ss"
export const standardFormatDate = "YYYY-MM-DD"
export const standardFormatTime = "HH:mm:ss"
// Format date for accessibility (how dates will be read)
export const formatDateAccessibility = "dddd Do MMMM YYYY"
export const formatTimeAccessibility = "hh:mm A"

const getLocalizedCurrentDate = () => {
  return moment.localeData().longDateFormat("L")
}

export const getDurationInDays = (fromValue, toValue, dateFormat = standardFormatDateTime) => {
  // Get the from and to string as a moment object
  const from = moment(fromValue, dateFormat)
  const to = moment(toValue, dateFormat)

  return Math.ceil(from.diff(to, "days", true))
}

export const isValid = (date, dateFormat = standardFormatDate) => {
  const newDate = moment(date, dateFormat, true)
  return newDate.isValid()
}

export const isInRange = (dateString, from, to) => {
  const dateFormat = getLocalizedCurrentDate()
  const date = getDateAsMoment(dateString, dateFormat)

  return date ? date.isBetween(from, to, "day", "[]") : false
}

// This function is to be used when we have a string date time
// and want to convert it into a moment object.
// (if no format is passed, it uses the standardFormatDate, which is the one used by the API
// but we can also pass another format - eg. to display dates according to the user locale format)
export const getDateAsMoment = (date, dateFormat = standardFormatDate) => {
  if (date) {
    const newDate = moment(date, dateFormat, true)
    return newDate.isValid() ? newDate : null
  }
  return null
}

/**
 * This function is to be used when we have a string date time
 * and want to convert it into a moment object
 * using the users browser locale to format it
 *
 * @param {string} dateString
 */
export const formatDateWithUserLocale = dateString => {
  const dateFormat = getLocalizedCurrentDate()
  return getDateAsMoment(dateString, dateFormat)
}

// This function is to be used when we have a moment object
// and want to convert it into a string date.
// (if no format is passed, it uses the standardFormatDate, which is the one used by the API
// but we can also pass another format - eg. to display dates according to the user locale format)
export const getMomentAsDate = (date, dateFormat = standardFormatDate) => {
  if (date) {
    return date.format(dateFormat)
  }
  return ""
}

// This function is to be used when we have two string (date and time)
// and want to convert it into a moment object.
export const getDateTimeAsDate = (date, time) => {
  if (date && time) {
    const dateMoment = moment(`${date}T${time}`, "YYYY-MM-DDTHH:mm:ssZ")
    return dateMoment.format("YYYY-MM-DDTHH:mm:ssZ")
  }
  return null
}

// Return date formatted as formatTo. If formatTo is empty, date will be formatted based on locale
export const formatDateTime = (date, formatFrom = standardFormatDateTime, formatTo, showTime) => {
  if (_.isEmpty(date)) {
    return ""
  }
  if (_.isEmpty(formatTo)) {
    // if formatTo is not defined, date will be displayed based on locale format date
    const dateMoment = moment(date)
    if (dateMoment.isValid()) {
      return showTime
        ? dateMoment.format("llll")
        : dateMoment.format("llll").replace(dateMoment.format("LT"), "")
    }
    return ""
  }
  const dateMoment = moment(date, formatFrom)
  return dateMoment.isValid() ? dateMoment.format(formatTo) : ""
}

export const formatDate = (
  date,
  formatFrom = standardFormatDate,
  formatTo = getLocalizedCurrentDate()
) => {
  return formatDateTime(date, formatFrom, formatTo, false)
}

const formatHoursMinutes = value => {
  return value > 9 ? `${value}` : `0${value}`
}

// This function receives the day, month and year as numbers or strings,
// and returns the date as a string in standard format.
export const getDateFromFields = (day, month, year, dateFormat = standardFormatDate) => {
  const date = moment.utc([year, month - 1, day])

  return date.isValid() ? getMomentAsDate(date, dateFormat) : ""
}

// This function receives date and time as strings and the date format.
// It returns the date and time merged into one string in the standard format.
// date = "01/03/2019", time = "09:00", formatFrom = "DD/MM/YYYY" => "2019-03-01T09:00"
export const appendDateTime = (date, time, formatFrom) => {
  const formattedDate = formatDate(date, formatFrom, standardFormatDate)

  return `${formattedDate}T${time}`
}

export const initializedTimesList = (interval = 30, timeFormat = standardFormatTime) => {
  const numberOfMinutesInADay = 24 * 60
  const numIterations = numberOfMinutesInADay / interval
  return _.times(numIterations, interation => {
    const timeInMinutes = interval * interation
    const hour = _.floor(timeInMinutes / 60)
    const min = timeInMinutes % 60
    const value = `${formatHoursMinutes(hour)}:${formatHoursMinutes(min)}`
    return { value, label: formatDate(value, standardFormatTime, timeFormat) }
  })
}

export const formattedDate = newDate => {
  const formattedDay = newDate.day ? String(newDate.day).padStart(2, "0") : ""
  const formattedMonth = newDate.month ? String(newDate.month).padStart(2, "0") : ""
  const formattedYear = newDate.year ? String(newDate.year).padStart(4, "0") : ""
  return `${formattedYear}-${formattedMonth}-${formattedDay}`
}
